import { Button, CircularProgress, Stack, Typography } from '@mui/material'
import { useTranslation } from 'hooks/useTranslation'
import CredentialsForm, {
  CredentialsFormValues
} from 'modules/authentication/components/CredentialsForm'
import OtpForm from 'modules/authentication/components/OtpForm'
import FormAlertError from 'modules/form/components/FormAlertError'
import usePasswordRequestContext from 'modules/passwordRequest/hooks/usePasswordRequestContext'
import { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { OAuthInteractionResponse } from 'services/api/iam/manager/interfaces/OAuthInteractionResponse'
import { OAuthConsentBody } from 'services/api/iam/manager/interfaces/OAuthConsentRequest'
import { useOAuthLogin } from 'services/api/iam/manager/mutations/useOAuthLogin'
import { useOAuthOtp } from 'services/api/iam/manager/mutations/useOAuthOtp'
import { useOAuthConsent } from 'services/api/iam/manager/mutations/useOAuthConsent'
import { useOAuthSwitchUser } from 'services/api/iam/manager/mutations/useOAuthSwitchUser'
import {
  oauthInteractionQueryKey,
  useGetOAuthInteraction
} from 'services/api/iam/manager/queries/useGetOAuthInteraction'
import OAuthConsent from './components/OAuthConsent/OAuthConsent'
import { useOAuthErrorMessage } from './hooks/useOAuthErrorMessage'

export default function OAuthInteraction() {
  const { id = '' } = useParams<{ id: string }>()
  return <OAuthInteractionContent key={id} id={id} />
}

function OAuthInteractionContent({ id }: { id: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const {
    email: [, setRecoveryEmail],
    oauthReturn: [, setOAuthReturn]
  } = usePasswordRequestContext()
  const query = useGetOAuthInteraction(id)
  const interaction = query.data
  const update = (data: OAuthInteractionResponse) => {
    queryClient.setQueryData(oauthInteractionQueryKey(data.id), data)
  }
  const login = useOAuthLogin({ onSuccess: update })
  const otp = useOAuthOtp({ onSuccess: update })
  const switchUser = useOAuthSwitchUser({ onSuccess: update })
  const consent = useOAuthConsent({
    onSuccess: result => window.location.assign(result.redirect_uri)
  })
  const [expired, setExpired] = useState(false)
  const expiresAt = interaction?.expires_at
  useEffect(() => {
    if (!expiresAt) return
    setExpired(false)
    const timeout = window.setTimeout(
      () => setExpired(true),
      Math.max(0, expiresAt * 1000 - Date.now())
    )
    return () => window.clearTimeout(timeout)
  }, [expiresAt])
  const busy =
    query.isFetching ||
    login.isLoading ||
    otp.isLoading ||
    consent.isLoading ||
    switchUser.isLoading
  const error = useOAuthErrorMessage(
    query.error || switchUser.error || consent.error || otp.error || login.error
  )
  const clearErrors = () => {
    login.reset()
    otp.reset()
    consent.reset()
    switchUser.reset()
  }
  const submitLogin = async ({ email, password }: CredentialsFormValues) => {
    if (!interaction || busy || expired) return
    clearErrors()
    await login
      .mutateAsync({ id, csrf: interaction.csrf, identifier: email, password })
      .catch(() => undefined)
  }
  const submitOtp = async (code: string) => {
    if (!interaction || busy || expired) return
    clearErrors()
    await otp.mutateAsync({ id, csrf: interaction.csrf, code }).catch(() => undefined)
  }
  const submitConsent = (body: OAuthConsentBody) => {
    if (!interaction || busy || expired) return
    clearErrors()
    consent.mutate({ id, csrf: interaction.csrf, ...body })
  }
  const recover = (email: string) => {
    if (!interaction || busy || expired) return
    setRecoveryEmail(email)
    setOAuthReturn({ interactionId: id, email, expiresAt: interaction.expires_at })
    navigate('/register-new-password')
  }
  return (
    <Stack gap="30px" aria-busy={busy}>
      {expired || error ? (
        <FormAlertError
          message={
            expired
              ? t(
                  'Esta autorização expirou ou foi encerrada. Volte ao aplicativo e inicie a conexão novamente.'
                )
              : error
          }
        />
      ) : null}
      {!interaction ? (
        error ? (
          <Button onClick={() => query.refetch()}>{t('Tentar novamente')}</Button>
        ) : (
          <CircularProgress aria-label={t('Carregando autorização')} />
        )
      ) : interaction.step === 'consent' ? (
        <OAuthConsent
          key={interaction.user?.email}
          interaction={interaction}
          busy={busy || expired}
          onSubmit={submitConsent}
          onCancel={() => submitConsent({ approved: false })}
          onSwitchUser={() => {
            if (busy || expired) return
            clearErrors()
            switchUser.mutate({ id, csrf: interaction.csrf })
          }}
        />
      ) : (
        <>
          <Stack gap="10px">
            <Typography variant="h4">
              {t(interaction.step === 'otp' ? 'Verificação em 2 etapas' : 'Entrar para autorizar')}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {interaction.client.name}
            </Typography>
          </Stack>
          {interaction.step === 'login' ? (
            <CredentialsForm
              busy={busy || expired}
              submitLabel={t('Continuar')}
              clearPasswordOnSubmit
              onSubmit={submitLogin}
              onRecover={recover}
            />
          ) : (
            <OtpForm busy={busy || expired} onSubmit={submitOtp} />
          )}
          <Button disabled={busy || expired} onClick={() => submitConsent({ approved: false })}>
            {t('Cancelar')}
          </Button>
        </>
      )}
    </Stack>
  )
}
